import React, {useState, useEffect} from "react";
import {makeStyles} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Title from "../dashboard/Title";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import SelectInput from "./input/SelectInput";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {Redirect, useParams} from "react-router-dom";
import {NotificationManager} from 'react-notifications';
import Paper from "@material-ui/core/Paper";
import TextInput from "./input/TextInput";
import DateTimeInput from "./input/DateTimeInput";
import {Grid} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Popup from "../presentation/Popup";
import AuthService from "../../services/auth.service";
import {useDashboardStyles, BRAND} from "../settings/dashboardStyles";

const useLocalStyles = makeStyles((theme) => ({
    formField: {
        marginBottom: theme.spacing(2),
        "& .MuiFormControl-root": {
            width: "100%",
        },
    },
    sectionHeading: {
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: "uppercase",
        color: BRAND.inkSoft,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        paddingBottom: theme.spacing(0.5),
        borderBottom: `1px solid ${BRAND.goldSoft}`,
    },
    formActionsBar: {
        marginTop: theme.spacing(3),
        display: "flex",
        gap: theme.spacing(1.5),
        justifyContent: "flex-end",
        flexWrap: "wrap",
        [theme.breakpoints.down("xs")]: {
            "& > button": {
                flex: "1 1 auto",
            },
        },
    },
    warningText: {
        color: "#a8324a",
        fontWeight: 600,
        fontSize: "1rem",
        marginBottom: theme.spacing(1),
    },
    warningSubtitle: {
        color: BRAND.deep,
        fontWeight: 600,
        marginTop: theme.spacing(1.5),
    },
    warningBody: {
        color: BRAND.inkSoft,
        fontSize: "0.92rem",
    },
}));

const normalizeProfileValues = (values) => [...new Set((values || [])
    .map((value) => (typeof value === "string" ? value : value?.name || value?.title || "").trim())
    .filter(Boolean))];

export default function EvidenceForm(props) {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();

    const [values, setValues] = useState({
        items: '',
        placement_fridge: null,
        car_id: '',
        temperature: '',
        disinfection: '',
        disinfection_date: null,
        autopsy: '',
        d_dangerous_disease: '',
        funeral_id: useParams().funeral_id,
        employee: '',
        employee_money: '',
        employee_transfer: '',
        employee_clothes: '',
        employee_funeral: '',
        placement_of_decedent: '',
        urn_number: '',
        cremation_datetime: null
    });

    const [, setOldValues] = useState({});
    const [show, setShow] = useState(false);
    const [, setSignatures] = useState({});
    const [success, setSuccess] = useState(false);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [automobileOptions, setAutomobileOptions] = useState([]);

    const handleChange = (event) => {
        const {name, value} = event.target;
        setValues((currentValues) => ({...currentValues, [name]: value}));
    }

    const handleAutocompleteChange = (name, value) => {
        setValues((currentValues) => ({...currentValues, [name]: value || ''}));
    }
    const params = useParams();
    const hasProperty = params.hasOwnProperty('evidence_id');

    useEffect(() => {
        const currentUser = AuthService.getCurrentUser();

        UserService.getSignatures(params.funeral_id).then(
            (response) => {
                setSignatures(response.data);
            }
        )
        if (currentUser?.id) {
            UserService.getUser(currentUser.id).then(
                (response) => {
                    setEmployeeOptions(normalizeProfileValues(response.data.employees));
                    setAutomobileOptions(normalizeProfileValues(response.data.automobiles));
                },
                (e) => {
                    if (e.response.status === 401) {
                        logOut(props);
                        NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                    }
                }
            );
        }
        if (hasProperty) {
            UserService.getEvidence(params.evidence_id).then(
                (response) => {
                    setValues((currentValues) => ({...currentValues, ...response.data}));
                    setOldValues(response.data);
                },
                (e) => {
                    if (e.response.status === 401) {
                        logOut(props);
                        NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                    }
                }
            );
        }
    }, [hasProperty, params.funeral_id, params.evidence_id, props]);

    const handleCreate = (e) => {
        e.preventDefault();
        UserService.createEvidence(values).then(
            () => {
                setSuccess(true);
                NotificationManager.success('Dáta o evidencií upravené.', 'Upravené', 5000);
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                } else {
                    NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                }
            }
        );
    };

    const yesNoOptions = [
        {value: 'Áno', title: 'Áno'},
        {value: 'Nie', title: 'Nie'},
    ];

    const autopsyOptions = [
        {value: 'NIE', title: 'NIE'},
        {value: 'ÁNO, Urobili sme zvoz a MÁME POHREB', title: 'ÁNO, Urobili sme zvoz a MÁME POHREB'},
        {value: 'ÁNO, INÁ pohrebná služba urobila zvoz a MÁME POHREB', title: 'ÁNO, INÁ pohrebná služba urobila zvoz a MÁME POHREB'},
        {value: 'ÁNO, urobili sme zvoz a NEMÁME POHREB', title: 'ÁNO, urobili sme zvoz a NEMÁME POHREB'},
    ];

    const renderAutocompleteField = (name, label, options) => (
        <Autocomplete
            freeSolo
            options={options}
            value={values[name] || ''}
            inputValue={values[name] || ''}
            onChange={(event, newValue) => handleAutocompleteChange(name, newValue)}
            onInputChange={(event, newInputValue) => handleAutocompleteChange(name, newInputValue)}
            renderInput={(params) => (
                <TextInput
                    {...params}
                    label={label}
                    name={name}
                    fullWidth
                />
            )}
        />
    );

    return (
        <form onSubmit={handleCreate}>
            <Container maxWidth="lg" className={classes.pageContainer}>
                <Paper className={classes.paper} elevation={0}>
                    <div className={classes.sectionHeader}>
                        <Title>Úprava dát pre evidenciu</Title>
                    </div>

                    <div className={localClasses.sectionHeading}>Doklady a stav</div>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <TextInput label="Odovzdané doklady a osobné veci" name={'items'} value={values.items || ''} onChange={handleChange} fullWidth multiline rows={3}/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <DateTimeInput label="Dátum a čas uloženia do chladiaceho zariadenia" name={'placement_fridge'} value={values.placement_fridge} onChange={handleChange}/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <SelectInput label="Pitva" name={'autopsy'} value={values.autopsy} options={autopsyOptions} onChange={handleChange}/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <SelectInput label="Úmrtie na nebezpečnú chorobu" name={'d_dangerous_disease'} value={values.d_dangerous_disease} options={yesNoOptions} onChange={handleChange}/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                {renderAutocompleteField('car_id', 'ŠPZ prepravujúceho vozidla', automobileOptions)}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <TextInput label="Teplota v chladiarenskom zariadení" name={'temperature'} value={values.temperature || ''} onChange={handleChange} fullWidth/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <TextInput label="Čistenie a dezinfekcia chladiaceho zariadenia a vozidla" name={'disinfection'} value={values.disinfection || ''} onChange={handleChange} fullWidth/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <DateTimeInput label="Dátum a čas - čistenie a dezinfekcia chladiaceho zariadenia a vozidla" name={'disinfection_date'} value={values.disinfection_date} onChange={handleChange}/>
                            </div>
                        </Grid>
                    </Grid>

                    <div className={localClasses.sectionHeading}>Zamestnanci</div>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                {renderAutocompleteField('employee', 'Kto vybavuje pohreb (zamestnanec)', employeeOptions)}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                {renderAutocompleteField('employee_money', 'Kto prevzal peniaze za pohreb (zamestnanec)', employeeOptions)}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                {renderAutocompleteField('employee_transfer', 'Kto bol po zosnulého (zamestnanec)', employeeOptions)}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                {renderAutocompleteField('employee_clothes', 'Kto obliekal zosnulého (zamestnanec)', employeeOptions)}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                {renderAutocompleteField('employee_funeral', 'Kto bol na pohrebe (zamestnanec)', employeeOptions)}
                            </div>
                        </Grid>
                    </Grid>

                    <div className={localClasses.sectionHeading}>Uloženie a kremácia</div>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <TextInput label="Miesto, kde sú uložené ľudské ostatky pred pohrebom" name={'placement_of_decedent'} value={values.placement_of_decedent || ''} onChange={handleChange} fullWidth/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <TextInput label="Číslo urny" name={'urn_number'} value={values.urn_number || ''} onChange={handleChange} fullWidth/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <DateTimeInput label="Dátum a čas kremácie" name={'cremation_datetime'} value={values.cremation_datetime} onChange={handleChange}/>
                            </div>
                        </Grid>
                    </Grid>
                </Paper>

                <div className={localClasses.formActionsBar}>
                    <Button
                        className={classes.secondaryAction}
                        onClick={() => setSuccess(true)}
                    >
                        Späť na detaily
                    </Button>
                    <Button
                        type='submit'
                        className={classes.primaryAction}
                    >
                        Uložiť zmeny
                    </Button>
                </div>
            </Container>

            <Popup
                title={'Upozornenie'}
                openPopup={show}
                setOpenPopup={setShow}
                maxW="sm"
            >
                <Typography className={localClasses.warningText}>
                    Pri úprave dát, ktoré sú uvedené pri podpísaných dokumentoch, sa podpis pri dokumente vymaže!
                </Typography>
                <Typography className={localClasses.warningSubtitle}>
                    Dáta pre Dokumenty na podpis
                </Typography>
                <Typography className={localClasses.warningBody}>
                    odovzdané doklady a osobné veci
                </Typography>
            </Popup>

            {success ? <Redirect to={{
                pathname: `/pohreb/${params.funeral_id}/info`,
                state: {from: props.location, number: 4}
            }}/> : <></>}
        </form>
    );
}
